'use client';

import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Upload,
  X,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Users,
  LogIn,
  UserCog,
} from 'lucide-react';
import { buildFormData } from '@/lib/formData';
import { apiPost } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import { setAuthRedirect } from '@/lib/auth-token';
import type {
  GameType,
  StaffRole,
  TournamentTeamPlayer,
  TournamentTeamStaff,
} from '../interfaces/tournaments';
import { GAME_MIN_PLAYERS, STAFF_ROLE_LABELS } from '../interfaces/tournaments';

interface PublicRegisterTeamProps {
  isOpen: boolean;
  tournamentId: string;
  tournamentSlug: string;
  gameType: GameType;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'team' | 'players' | 'staff' | 'review';

const steps: Step[] = ['team', 'players', 'staff', 'review'];
const stepLabels: Record<Step, string> = {
  team: 'Equipo',
  players: 'Jugadores',
  staff: 'Staff',
  review: 'Confirmar',
};

/** Override explícito de sm:max-w-lg del Dialog base */
const REGISTER_DIALOG_CLASS =
  'w-[min(calc(100vw-1.5rem),72rem)] max-w-[min(calc(100vw-1.5rem),72rem)] sm:max-w-6xl';

const emptyPlayer = () => ({
  player_name: '',
  player_email: '',
  player_ign: '',
  player_role: '',
  riot_id: '',
  discord_tag: '',
  position: 0,
  _formId: crypto.randomUUID(),
});

const emptyStaff = () => ({
  staff_name: '',
  staff_email: '',
  staff_role: 'coach' as const,
  _formId: crypto.randomUUID(),
});

export const PublicRegisterTeam: React.FC<PublicRegisterTeamProps> = ({
  isOpen,
  tournamentId,
  tournamentSlug,
  gameType,
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState<Step>('team');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const minPlayers = GAME_MIN_PLAYERS[gameType];
  const isRiotGame = gameType === 'valorant' || gameType === 'league_of_legends';

  const [formData, setFormData] = useState({
    team_name: '',
    team_tag: '',
    captain_name: '',
    captain_email: '',
  });

  const [players, setPlayers] = useState<
    (Omit<TournamentTeamPlayer, 'id' | 'tournament_team_id' | 'created_at' | 'updated_at'> & { _formId: string })[]
  >([]);

  const [staff, setStaff] = useState<
    (Omit<TournamentTeamStaff, 'id' | 'tournament_team_id' | 'created_at' | 'updated_at'> & { _formId: string })[]
  >([]);

  const [prevRegisterState, setPrevRegisterState] = useState<{
    isOpen: boolean;
    userId: string | undefined;
  }>({ isOpen: false, userId: undefined });

  if (isOpen !== prevRegisterState.isOpen || user?.id !== prevRegisterState.userId) {
    const wasOpen = prevRegisterState.isOpen;
    setPrevRegisterState({ isOpen, userId: user?.id });
    if (!isOpen && wasOpen) {
      setFormData({ team_name: '', team_tag: '', captain_name: '', captain_email: '' });
      setPlayers([]);
      setStaff([]);
      setLogoFile(null);
      setLogoPreview(null);
      setCurrentStep('team');
    } else if (isOpen) {
      setCurrentStep('team');
      if (user) {
        setFormData((p) => ({
          ...p,
          captain_name: user.name || p.captain_name,
          captain_email: user.email || p.captain_email,
        }));
      }
    }
  }

  useEffect(() => {
    if (!isOpen && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({ team_name: '', team_tag: '', captain_name: '', captain_email: '' });
    setPlayers([]);
    setStaff([]);
    setLogoFile(null);
    setLogoPreview(null);
    setCurrentStep('team');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleLoginRedirect = () => {
    const returnPath = `/tournaments/${tournamentSlug}`;
    setAuthRedirect(returnPath);
    navigate(`/login?return=${encodeURIComponent(returnPath)}`);
    onClose();
  };

  const canGoNext = useMemo(() => {
    switch (currentStep) {
      case 'team':
        return (
          formData.team_name.trim() !== '' &&
          formData.captain_name.trim() !== '' &&
          formData.captain_email.includes('@')
        );
      case 'players':
        if (players.length < minPlayers) return false;
        if (isRiotGame) {
          return players.every(
            (p) =>
              p.player_name.trim() !== '' &&
              p.riot_id?.trim() &&
              p.discord_tag?.trim(),
          );
        }
        return players.every((p) => p.player_name.trim() !== '');
      case 'staff':
        return true;
      case 'review':
        return true;
      default:
        return false;
    }
  }, [currentStep, formData, players, minPlayers, isRiotGame]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await apiPost(
        `/public/tournaments/${tournamentId}/register`,
        buildFormData(
          {
            team_name: formData.team_name,
            team_tag: formData.team_tag.slice(0, 4) || '',
            captain_name: formData.captain_name,
            captain_email: formData.captain_email,
            players: players
              .filter((p) => p.player_name.trim())
              .map((p, i) => ({
                player_name: p.player_name,
                player_email: p.player_email || null,
                player_ign: p.player_ign || null,
                player_role: p.player_role || null,
                riot_id: p.riot_id || null,
                discord_tag: p.discord_tag || null,
                position: i + 1,
              })),
            staff: staff
              .filter((s) => s.staff_name.trim())
              .map((s) => ({
                staff_name: s.staff_name,
                staff_email: s.staff_email || null,
                staff_role: s.staff_role,
              })),
          },
          logoFile,
        ),
      );
      toast.success('¡Equipo inscrito correctamente!');
      resetForm();
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Error al registrar';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className={REGISTER_DIALOG_CLASS}>
          <div className="flex min-h-[280px] items-center justify-center py-12">
            <p className="text-muted-foreground">Verificando sesión...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!isAuthenticated) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className={`${REGISTER_DIALOG_CLASS} text-center`}>
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="font-display text-2xl">Inicia sesión para inscribirte</DialogTitle>
            <DialogDescription className="text-base">
              Necesitas una cuenta de CoreUtils (Google o Discord) para registrar tu equipo en el torneo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 px-6 pb-8">
            <LogIn className="mx-auto h-14 w-14 text-primary/60" />
            <Button onClick={handleLoginRedirect} className="h-11 w-full text-base">
              Iniciar sesión
            </Button>
            <Button variant="outline" onClick={handleClose} className="h-11 w-full">
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const currentIndex = steps.indexOf(currentStep);
  const isLastStep = currentIndex === steps.length - 1;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className={`${REGISTER_DIALOG_CLASS} flex max-h-[min(92vh,920px)] flex-col gap-0 overflow-hidden p-0`}
      >
        <DialogHeader className="shrink-0 border-b border-border/60 px-6 py-5 text-left">
          <DialogTitle className="font-display text-2xl">Inscribir equipo</DialogTitle>
          <DialogDescription className="text-base">
            Registrado como <span className="font-medium text-foreground">{user?.email}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="shrink-0 border-b border-border/40 bg-muted/20 px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-2">
            {steps.map((step, i) => (
              <div key={step} className="flex min-w-0 flex-1 items-center">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold ${
                    i <= currentIndex
                      ? 'border-primary bg-primary/15 text-primary'
                      : 'border-muted-foreground/25 text-muted-foreground'
                  }`}
                >
                  {i < currentIndex ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                </div>
                <span className="ml-2 hidden truncate text-sm font-medium sm:inline">
                  {stepLabels[step]}
                </span>
                {i < steps.length - 1 && (
                  <div
                    className={`mx-2 h-0.5 min-w-[12px] flex-1 ${i < currentIndex ? 'bg-primary' : 'bg-border'}`}
                  />
                )}
              </div>
            ))}
          </div>
          <p className="mt-3 text-center text-xs text-muted-foreground sm:hidden">
            Paso {currentIndex + 1} de {steps.length}: {stepLabels[currentStep]}
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 custom-scrollbar">
        {currentStep === 'team' && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Nombre del equipo *</Label>
                <Input
                  className="h-10"
                  value={formData.team_name}
                  onChange={(e) => setFormData((p) => ({ ...p, team_name: e.target.value }))}
                  placeholder="Ej: Team Liquid"
                />
              </div>
              <div className="space-y-2">
                <Label>Tag (máx. 4 caracteres)</Label>
                <Input
                  value={formData.team_tag}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, team_tag: e.target.value.slice(0, 4) }))
                  }
                  placeholder="TL"
                  maxLength={4}
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Capitán / contacto *</Label>
                <Input
                  value={formData.captain_name}
                  onChange={(e) => setFormData((p) => ({ ...p, captain_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Email de contacto *</Label>
                <Input
                  type="email"
                  value={formData.captain_email}
                  onChange={(e) => setFormData((p) => ({ ...p, captain_email: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Logo (opcional)</Label>
              {logoPreview ? (
                <div className="relative inline-block">
                  <img 
                    src={logoPreview} 
                    alt="" 
                    width={80}
                    height={80}
                    loading="lazy"
                    className="w-20 h-20 rounded-lg object-cover border" 
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute -top-2 -right-2 h-6 w-6 p-0"
                    onClick={() => {
                      setLogoFile(null);
                      setLogoPreview(null);
                    }}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    aria-label="Subir imagen del equipo"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setLogoFile(f);
                      const r = new FileReader();
                      r.onload = () => setLogoPreview(r.result as string);
                      r.readAsDataURL(f);
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Subir logo
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Step: players */}
        {currentStep === 'players' && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Mínimo {minPlayers} jugadores ({players.length}/{minPlayers})
              </p>
              <Button type="button" variant="outline" size="sm" onClick={() => setPlayers([...players, emptyPlayer()])}>
                <Plus className="w-4 h-4 mr-1" />
                Agregar
              </Button>
            </div>
            {players.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center">
                  <Users className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
                  <Button
                    variant="outline"
                    onClick={() =>
                      setPlayers(Array.from({ length: minPlayers }, (_, i) => ({
                        ...emptyPlayer(),
                        position: i + 1,
                      })))
                    }
                  >
                    Agregar {minPlayers} jugadores
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
              {players.map((player, index) => (
                <Card key={player._formId} className="surface-gaming">
                  <CardContent className="space-y-3 p-5">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">Jugador {index + 1}</Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setPlayers(players.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                    <Input
                      placeholder="Nombre completo *"
                      value={player.player_name}
                      onChange={(e) => {
                        const u = [...players];
                        u[index] = { ...u[index], player_name: e.target.value };
                        setPlayers(u);
                      }}
                    />
                    {isRiotGame && (
                      <div className="grid sm:grid-cols-2 gap-3">
                        <Input
                          placeholder="Riot ID (nombre#tag) *"
                          value={player.riot_id || ''}
                          onChange={(e) => {
                            const u = [...players];
                            u[index] = { ...u[index], riot_id: e.target.value };
                            setPlayers(u);
                          }}
                        />
                        <Input
                          placeholder="Discord (usuario) *"
                          value={player.discord_tag || ''}
                          onChange={(e) => {
                            const u = [...players];
                            u[index] = { ...u[index], discord_tag: e.target.value };
                            setPlayers(u);
                          }}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              </div>
            )}
          </div>
        )}

        {/* Step: staff */}
        {currentStep === 'staff' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Staff (opcional)</Label>
                <p className="text-xs text-muted-foreground">
                  Entrenadores, managers y otros miembros del staff
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setStaff([...staff, emptyStaff()])}
              >
                <Plus className="w-4 h-4 mr-1" />
                Agregar
              </Button>
            </div>
            {staff.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center">
                  <UserCog className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground mb-3">No hay staff agregado</p>
                  <Button variant="outline" onClick={() => setStaff([emptyStaff()])}>
                    Agregar staff
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
              {staff.map((member, index) => (
                <Card key={member._formId} className="surface-gaming">
                  <CardContent className="space-y-3 p-5">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">{STAFF_ROLE_LABELS[member.staff_role]}</Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setStaff(staff.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Input
                        placeholder="Nombre completo *"
                        value={member.staff_name}
                        onChange={(e) => {
                          const u = [...staff];
                          u[index] = { ...u[index], staff_name: e.target.value };
                          setStaff(u);
                        }}
                      />
                      <Select
                        value={member.staff_role}
                        onValueChange={(value) => {
                          const u = [...staff];
                          u[index] = { ...u[index], staff_role: value as StaffRole };
                          setStaff(u);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STAFF_ROLE_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      type="email"
                      placeholder="Email (opcional)"
                      value={member.staff_email || ''}
                      onChange={(e) => {
                        const u = [...staff];
                        u[index] = { ...u[index], staff_email: e.target.value };
                        setStaff(u);
                      }}
                    />
                  </CardContent>
                </Card>
              ))}
              </div>
            )}
          </div>
        )}

        {/* Step: review */}
        {currentStep === 'review' && (
          <div className="space-y-5">
            <Card className="surface-gaming">
              <CardContent className="space-y-3 p-5 text-sm sm:text-base">
                <p><span className="font-medium">Equipo:</span> {formData.team_name} {formData.team_tag && `[${formData.team_tag}]`}</p>
                <p><span className="font-medium">Capitán:</span> {formData.captain_name}</p>
                <p><span className="font-medium">Email:</span> {formData.captain_email}</p>
                <p><span className="font-medium">Jugadores:</span> {players.length}</p>
              </CardContent>
            </Card>
            <div className="grid gap-2 sm:grid-cols-2">
              {players.map((p) => (
                <div key={p._formId} className="rounded-lg border border-border/60 bg-muted/30 px-4 py-3 text-sm">
                  <span className="font-medium">{p.player_name}</span>
                  {p.riot_id && <span className="ml-2 text-muted-foreground">· {p.riot_id}</span>}
                </div>
              ))}
            </div>
            {staff.filter((s) => s.staff_name.trim()).length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Staff</p>
                {staff
                  .filter((s) => s.staff_name.trim())
                  .map((s) => (
                    <div
                      key={s._formId}
                      className="flex items-center justify-between text-sm px-3 py-2 rounded-md bg-muted/40"
                    >
                      <span className="font-medium">{s.staff_name}</span>
                      <Badge variant="outline">{STAFF_ROLE_LABELS[s.staff_role]}</Badge>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        </div>

        <DialogFooter className="shrink-0 flex-row justify-between gap-3 border-t border-border/60 bg-muted/10 px-4 py-4 sm:px-6">
          <div>
            {currentIndex > 0 && (
              <Button variant="outline" className="h-10" onClick={() => setCurrentStep(steps[currentIndex - 1])} disabled={isSubmitting}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="h-10" onClick={handleClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            {!isLastStep ? (
              <Button
                className="h-10 px-6"
                onClick={() => setCurrentStep(steps[currentIndex + 1])}
                disabled={!canGoNext}
              >
                Siguiente
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button className="h-10 px-6" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Inscribiendo...' : 'Confirmar inscripción'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
